import { PublicRoutes } from "@/constants/routes";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link, Outlet, useLocation } from "react-router";

// Título por tela do fluxo de autenticação.
//
// NOTA (2026-07-12): esta constante antes alimentava um `document.title =`
// manual via useEffect, com o comentário "react-helmet-async não atualiza o
// <title> sob o React 19". Investigando para o fix de noindex desta rota
// (ver AUDIT no relatório do /seo), a causa real era outra: o <Helmet
// defaultTitle="Fidexa" titleTemplate="%s | Fidexa"> global (src/App.tsx)
// roda seu PRÓPRIO commit (deferido em rAF) depois do nosso useEffect e
// SOBRESCREVE qualquer `document.title` setado por fora do Helmet de volta
// para o default — confirmado empiricamente: o HTML prerenderizado de /auth
// saía sempre com <title>Fidexa</title> (o default), nunca com o título por
// tela. A mesma tentativa de setar `<meta name="robots">` direto no DOM
// também falhava (a tag sumia por completo): ao marcar o node com
// `data-rh="true"` para imitar o marcador do Helmet, o Helmet passou a
// "adotar" essa tag como sua e a removeu no commit seguinte, por não haver
// nenhum <Helmet> declarando-a. Ou seja: lutar contra o Helmet por fora
// perde a corrida. A correção é usar o próprio <Helmet> aqui (como as
// demais páginas do projeto, ex. crediario.tsx) — ele então gerencia
// title/meta de forma coerente com a instância global, sem sobrescrita.
const AUTH_TITLES: Record<string, string> = {
  [PublicRoutes.AUTH]: "Acessar conta",
  [PublicRoutes.AUTH_OTP]: "Entrar com WhatsApp",
  [PublicRoutes.AUTH_OTP_CODE]: "Confirmar código",
  [PublicRoutes.AUTH_CHANGE_PASSWORD]: "Alterar senha",
  [PublicRoutes.AUTH_FORGOT_PASSWORD]: "Redefinir senha",
  [PublicRoutes.AUTH_FORGOT_PASSWORD_PHONE]: "Esqueci minha senha",
  [PublicRoutes.AUTH_FORGOT_PASSWORD_CODE]: "Confirmar código",
};

export const AuthLayout = () => {
  const { pathname } = useLocation();
  // No login (entrada do fluxo) o "voltar" leva à home; nas demais telas
  // volta para o login.
  const isLogin = pathname === PublicRoutes.AUTH;
  const backTo = isLogin ? PublicRoutes.HOME : PublicRoutes.AUTH;
  const backLabel = isLogin ? "Voltar para a home" : "Voltar para o login";
  const title = AUTH_TITLES[pathname] ?? "Acessar conta";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        {/* Tela de login/OTP/recuperação de senha: indexada e recebendo
            clique orgânico no GSC (27+5 cliques / 492 impressões em 3
            meses) sem ser conteúdo de busca. noindex confirmado
            explicitamente pelo founder para toda a árvore /auth/*. */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <main className="flex min-h-dvh w-full bg-white">
        {/* Painel lateral — reservado para imagem futuramente */}
        <aside className="relative hidden flex-1 overflow-hidden bg-primary-v2-900 lg:flex">
          <div className="auth-gradient-animated absolute inset-0" />
          <div className="relative z-10 flex flex-col justify-end p-12">
            <p className="text-title-xl text-white">
              Recupere seu crediário
              <br />e venda mais com a Fidexa.
            </p>
            <p className="mt-4 max-w-md text-base text-white/70">
              A plataforma que ajuda o varejo a cobrar, recuperar e fidelizar
              clientes.
            </p>
          </div>
        </aside>

        {/* Painel do formulário */}
        <section className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6">
          <div className="flex w-full max-w-[420px] flex-col gap-8">
            <img
              src="/images/logo-fidexa-principal.svg"
              alt="Fidexa"
              width={140}
              className="h-auto"
            />
            <Link
              to={backTo}
              className="flex w-fit cursor-pointer items-center gap-1 text-sm text-gray-500 transition-colors hover:text-black"
            >
              <ArrowLeft className="size-4" />
              {backLabel}
            </Link>
            <Outlet />
          </div>
        </section>
      </main>
    </>
  );
};
